import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { 
  CurrencyDollarIcon,
  GiftIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon,
  SparklesIcon,
  TrophyIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChevronRightIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

const VipDashboard = () => {

  // VIP 정보
  const { data: vipInfo, isLoading: vipLoading, error: vipError } = useQuery(
    'vipInfo',
    () => api.get('/vip/info').then(res => res.data),
    {
      onSuccess: (data) => {
        console.log('VIP Info:', data);
      },
      onError: (error) => {
        console.error('VIP Info Error:', error);
      },
      refetchOnWindowFocus: false,
      retry: 2
    }
  );

  // 내 프로젝트
  const { data: myProjects } = useQuery(
    'vipProjects',
    () => api.get('/projects?my=true').then(res => res.data)
  );

  // 리워드 히스토리
  const { data: rewardHistory } = useQuery(
    'rewardHistory',
    () => api.get('/vip/rewards?limit=5').then(res => res.data?.rewards || []),
    {
      onSuccess: (data) => {
        console.log('Reward History:', data);
      }
    }
  );

  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return '0원';
    const num = parseInt(amount);
    if (num >= 100000000) return `${(num / 100000000).toFixed(1)}억원`;
    if (num >= 10000000) return `${Math.floor(num / 10000000)}천만원`;
    if (num >= 10000) return `${Math.floor(num / 10000)}만원`;
    return `${num.toLocaleString()}원`;
  };
  
  // 디버깅
  console.log('VIP Dashboard - vipInfo:', vipInfo);
  console.log('VIP Dashboard - monthlyEarnings:', vipInfo?.monthlyEarnings);

  return (
    <div className="space-y-6 pb-20">
      {/* VIP 리워드 현황 - 개선된 디자인 */}
      <div className="relative bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-6 text-white overflow-hidden">
        {/* 배경 패턴 */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-white rounded-full"></div>
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-white rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white rounded-full"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="mb-3">
                <h1 className="text-xl font-bold">VIP 고객님</h1>
                <p className="text-sm opacity-90">{format(new Date(), 'yyyy년 M월 d일 EEEE', { locale: ko })}</p>
              </div>
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-base sm:text-lg font-bold mb-1">
                    {formatCurrency(vipInfo?.current_balance)}
                  </p>
                  <p className="text-sm opacity-90">사용 가능 포인트</p>
                </div>
                <SparklesIcon className="h-8 w-8 opacity-50" />
              </div>
            </div>
            <Link 
              to="/projects"
              className="group bg-white/20 backdrop-blur text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl text-sm sm:text-base font-medium hover:bg-white/30 transition-all hover:scale-105 flex items-center gap-2"
            >
              <DocumentTextIcon className="h-4 sm:h-5 w-4 sm:w-5" />
              프로젝트
            </Link>
          </div>
          
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrophyIcon className="h-5 w-5 opacity-70" />
                <p className="text-sm opacity-90">누적 적립</p>
              </div>
              <p className="text-base sm:text-lg font-bold">{formatCurrency(vipInfo?.total_earned)}</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <FireIcon className="h-5 w-5 opacity-70" />
                <p className="text-sm opacity-90">적립률</p>
              </div>
              <p className="text-base sm:text-lg font-bold">{vipInfo?.reward_rate || 3}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* 프로젝트 현황 - 개선된 디자인 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/projects?status=contract"
          className="group relative bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-5 border border-amber-200 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 overflow-hidden block"
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-amber-200/20 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">계약</p>
                <p className="text-3xl font-bold text-gray-900 mt-1 group-hover:scale-110 transition-transform origin-left">
                  {myProjects?.projects?.filter(p => p.status === 'contract').length || 0}
                </p>
                <p className="text-xs text-amber-600 mt-1">계약된 프로젝트</p>
              </div>
              <div className="bg-amber-200/50 p-3 rounded-xl group-hover:bg-amber-300/50 transition-colors">
                <ClockIcon className="h-6 w-6 text-amber-700" />
              </div>
            </div>
          </div>
        </Link>

        <Link
          to="/projects?status=completed"
          className="group relative bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-5 border border-emerald-200 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 overflow-hidden block"
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-200/20 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">완료</p>
                <p className="text-3xl font-bold text-gray-900 mt-1 group-hover:scale-110 transition-transform origin-left">
                  {myProjects?.projects?.filter(p => p.status === 'completed').length || 0}
                </p>
                <p className="text-xs text-emerald-600 mt-1">성공적으로 완료됨</p>
              </div>
              <div className="bg-emerald-200/50 p-3 rounded-xl group-hover:bg-emerald-300/50 transition-colors">
                <CheckCircleIcon className="h-6 w-6 text-emerald-700" />
              </div>
            </div>
          </div>
        </Link>

        <Link
          to="/projects"
          className="group relative bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-5 border border-purple-200 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 overflow-hidden block"
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-purple-200/20 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">전체</p>
                <p className="text-3xl font-bold text-gray-900 mt-1 group-hover:scale-110 transition-transform origin-left">
                  {myProjects?.total || 0}
                </p>
                <p className="text-xs text-purple-600 mt-1">누적 프로젝트 수</p>
              </div>
              <div className="bg-purple-200/50 p-3 rounded-xl group-hover:bg-purple-300/50 transition-colors">
                <DocumentTextIcon className="h-6 w-6 text-purple-700" />
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* 리워드 적립 내역 & 차트 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 최근 리워드 내역 - 개선된 디자인 */}
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 bg-white/70 backdrop-blur border-b border-purple-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <GiftIcon className="h-5 w-5 text-purple-600" />
                </div>
                최근 리워드 내역
              </h3>
              <Link to="/vip/rewards" className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1 group">
                전체보기
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {rewardHistory?.length > 0 ? rewardHistory.map(reward => (
              <div key={reward.id} className="group bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-purple-200/50 hover:shadow-md transition-all hover:scale-[1.01]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      reward.transaction_type === 'earn' 
                        ? 'bg-gradient-to-br from-green-100 to-emerald-100' 
                        : 'bg-gradient-to-br from-red-100 to-pink-100'
                    }`}>
                      {reward.transaction_type === 'earn' ? (
                        <ArrowUpIcon className="h-5 w-5 text-green-600" />
                      ) : (
                        <ArrowDownIcon className="h-5 w-5 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {reward.transaction_type === 'earn' ? '포인트 적립' : '포인트 사용'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {reward.description}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold whitespace-nowrap ${
                      reward.transaction_type === 'earn' 
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent' 
                        : 'bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent'
                    }`}>
                      {reward.transaction_type === 'earn' ? '+' : '-'}{formatCurrency(reward.amount)}
                    </p>
                    <p className="text-xs text-gray-500 whitespace-nowrap">
                      {format(new Date(reward.created_at), 'M월 d일 HH:mm', { locale: ko })}
                    </p>
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center py-12">
                <GiftIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">아직 리워드 내역이 없습니다</p>
              </div>
            )}
          </div>
        </div>

        {/* 월별 적립 차트 - 개선된 디자인 */}
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 bg-white/70 backdrop-blur border-b border-indigo-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <div className="bg-indigo-100 p-2 rounded-lg">
                  <ChartBarIcon className="h-5 w-5 text-indigo-600" />
                </div>
                월별 적립 추이
              </h3>
              <span className="text-sm text-indigo-600 font-medium">
                최근 6개월
              </span>
            </div>
          </div>
          <div className="p-6">
            {vipLoading ? (
              <div className="flex flex-col items-center justify-center h-48">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-3"></div>
                <p className="text-gray-500">데이터를 불러오는 중...</p>
              </div>
            ) : vipInfo?.monthlyEarnings?.length > 0 ? (
              <>
                <div className="px-2">
                  {/* 차트 영역 */}
                  <div className="h-48 flex items-end justify-between gap-2 mb-2">
                    {vipInfo.monthlyEarnings.slice(-6).map((month, index) => {
                      const recentData = vipInfo.monthlyEarnings.slice(-6);
                      const maxAmount = Math.max(...recentData.map(m => parseInt(m.amount) || 0));
                      const heightPercent = maxAmount > 0 ? (month.amount / maxAmount) * 100 : 0;
                      const isHighest = month.amount === maxAmount && month.amount > 0;
                      
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center justify-end group cursor-pointer relative">
                          {/* 툴팁 */}
                          <div className="absolute -top-8 opacity-0 group-hover:opacity-100 transition-all duration-200 z-10">
                            <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap">
                              <div className="font-bold">{parseInt(month.amount || 0).toLocaleString()}원</div>
                            </div>
                            <div className="w-2 h-2 bg-gray-900 rotate-45 absolute -bottom-1 left-1/2 -translate-x-1/2"></div>
                          </div>
                          
                          {/* 바 차트 */}
                          <div 
                            className={`w-full rounded-t-xl transition-all duration-700 relative overflow-hidden ${
                              isHighest 
                                ? 'bg-gradient-to-t from-purple-600 to-purple-400 shadow-lg' 
                                : 'bg-gradient-to-t from-indigo-500 to-indigo-400'
                            } hover:shadow-xl transform hover:-translate-y-1`}
                            style={{ 
                              height: heightPercent > 0 ? `${Math.max(heightPercent * 1.8, 30)}px` : '4px',
                              maxHeight: '180px'
                            }}
                          >
                            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity"></div>
                            {isHighest && parseInt(month.amount) > 0 && (
                              <div className="absolute top-2 left-1/2 -translate-x-1/2">
                                <TrophyIcon className="h-4 w-4 text-white/80" />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* 월 라벨 영역 - 모든 월이 같은 높이에 정렬 */}
                  <div className="flex justify-between gap-2">
                    {vipInfo.monthlyEarnings.slice(-6).map((month, index) => {
                      const amount = parseInt(month.amount || 0);
                      return (
                        <div key={index} className="flex-1 text-center">
                          <span className="text-sm font-medium text-gray-700 block">{month.month}월</span>
                          <p className="text-xs text-gray-500 mt-0.5 whitespace-nowrap">
                            {(() => {
                              if (amount === 0) return '0원';
                              if (amount >= 100000000) return `${(amount / 100000000).toFixed(1)}억`;
                              if (amount >= 10000000) return `${Math.floor(amount / 10000000)}천만`;
                              if (amount >= 10000) return `${Math.floor(amount / 10000)}만`;
                              return `${amount.toLocaleString()}원`;
                            })()}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* 통계 요약 */}
                <div className="bg-white/50 rounded-xl p-4 grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-xs text-gray-600">월 평균</p>
                    <p className="text-lg font-bold text-gray-900 whitespace-nowrap">
                      {(() => {
                        const avg = vipInfo.monthlyEarnings.slice(-6).reduce((sum, m) => sum + (parseInt(m.amount) || 0), 0) / Math.min(vipInfo.monthlyEarnings.length, 6);
                        if (avg >= 100000000) return `${(avg / 100000000).toFixed(1)}억`;
                        if (avg >= 10000000) return `${Math.floor(avg / 10000000)}천만`;
                        if (avg >= 10000) return `${(avg / 10000).toFixed(1)}만`;
                        return `${Math.floor(avg).toLocaleString()}원`;
                      })()}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600">최고 기록</p>
                    <p className="text-lg font-bold text-purple-600 whitespace-nowrap">
                      {(() => {
                        const max = Math.max(...vipInfo.monthlyEarnings.slice(-6).map(m => parseInt(m.amount) || 0));
                        if (max >= 100000000) return `${(max / 100000000).toFixed(1)}억`;
                        if (max >= 10000000) return `${Math.floor(max / 10000000)}천만`;
                        if (max >= 10000) return `${Math.floor(max / 10000)}만`;
                        return `${max.toLocaleString()}원`;
                      })()}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-48">
                <ChartBarIcon className="h-12 w-12 text-gray-300 mb-3" />
                <p className="text-gray-500">리워드 내역이 없습니다</p>
                {vipError && (
                  <p className="text-xs text-red-500 mt-2">데이터 로드 오류</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 내 프로젝트 목록 - 개선된 디자인 */}
      <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl shadow-xl overflow-hidden">
        <div className="p-6 bg-white/70 backdrop-blur border-b border-blue-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <div className="bg-blue-100 p-2 rounded-lg">
                <DocumentTextIcon className="h-5 w-5 text-blue-600" />
              </div>
              프로젝트
            </h3>
            <Link to="/projects" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 group">
              전체보기
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </Link>
          </div>
        </div>
        <div className="p-6 space-y-3">
          {myProjects?.projects?.length > 0 ? myProjects.projects.slice(0, 5).map(project => (
            <Link 
              key={project.id} 
              to={`/projects/${project.id}`}
              className="group block bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-blue-200/50 hover:shadow-lg transition-all hover:scale-[1.01] hover:border-blue-300"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-700 transition-colors">
                    {project.title}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <p className="text-xs text-gray-600">
                      {project.contractor_name || '미배정'}
                    </p>
                    <span className="text-gray-400">•</span>
                    <p className="text-xs text-gray-600">
                      {format(new Date(project.created_at), 'M월 d일', { locale: ko })}
                    </p>
                    {project.status === 'completed' && project.reward_amount && (
                      <>
                        <span className="text-gray-400">•</span>
                        <p className="text-xs font-medium bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                          +{project.reward_amount.toLocaleString()}원 적립
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    project.status === 'completed' ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700' :
                    project.status === 'contract' ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {project.status_text}
                  </span>
                  <ChevronRightIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </Link>
          )) : (
            <div className="text-center py-12">
              <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">아직 프로젝트가 없습니다</p>
              <Link to="/projects/new" className="mt-3 inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
                새 프로젝트 만들기
                <ChevronRightIcon className="h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* 빠른 액션 - 개선된 디자인 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/projects" className="group relative overflow-hidden bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 text-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="relative z-10">
            <div className="bg-white/20 backdrop-blur w-12 h-12 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <DocumentTextIcon className="h-6 w-6" />
            </div>
            <p className="font-bold">프로젝트</p>
          </div>
        </Link>
        
        <Link to="/vip/rewards" className="group relative overflow-hidden bg-gradient-to-br from-purple-500 via-purple-600 to-pink-700 text-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="relative z-10">
            <div className="bg-white/20 backdrop-blur w-12 h-12 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <GiftIcon className="h-6 w-6" />
            </div>
            <p className="font-bold">리워드 내역</p>
          </div>
        </Link>
        
        <Link 
          to="/vip"
          className="group relative overflow-hidden bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 block"
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="relative z-10">
            <div className="bg-white/20 backdrop-blur w-12 h-12 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <BanknotesIcon className="h-6 w-6" />
            </div>
            <p className="font-bold">포인트 인출</p>
          </div>
        </Link>
        
        <Link to="/profile" className="group relative overflow-hidden bg-gradient-to-br from-amber-500 via-amber-600 to-orange-700 text-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="relative z-10">
            <div className="bg-white/20 backdrop-blur w-12 h-12 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <ArrowTrendingUpIcon className="h-6 w-6" />
            </div>
            <p className="font-bold">내 정보</p>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default VipDashboard;